import type { PathSession } from '~/pages/scorm/SessionPathView'
import { Award, Clock, Loader } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Progress } from '~/components/ui/progress'
import { brandTint, TEXT_SAFE_TINT } from '~/helpers/branding/brand-backdrop'
import { useBranding } from '~/helpers/branding/branding-context'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { canDownloadCertificate } from '~/helpers/learner/certificate-notice'
import { completionStatus, progressPct } from '~/helpers/learner/session-view'
import { tourAnchor } from '~/helpers/tour/anchors'
import CertificateLockNotice from '~/pages/scorm/components/CertificateLockNotice'
import { useStatusMeta } from '~/pages/scorm/components/useStatusMeta'

type IProps = {
  session: PathSession
  done: number
  total: number
  /** Temps restant estimé, déjà mis en mots ; vide = inconnu. */
  remaining: string | null
  onDownloadCertificate: () => void
  downloadingCertificate: boolean
}

/**
 * L'en-tête d'un parcours : nom, avancement, mode de progression, attestation ou
 * ce qu'il lui manque (#10).
 *
 * Même teinte légère que le bandeau d'accueil (#307), `TEXT_SAFE_TINT`, dont le
 * plafond garde le texte secondaire AA : l'écran du parcours se reconnaît comme
 * la suite de l'accueil, sans que la marque y fasse un aplat.
 */
function PathHeader({ session, done, total, remaining, onDownloadCertificate, downloadingCertificate }: IProps) {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()
  const meta = useStatusMeta()(completionStatus(session))

  return (
    <div
      className="mb-6 flex flex-col gap-4 rounded-2xl border border-border px-5 py-5 sm:px-7 sm:py-6"
      style={{ background: brandTint(TEXT_SAFE_TINT.primary) }}
      {...tourAnchor('path.header')}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <Badge variant={meta.badgeVariant} className="mb-2">{meta.badgeLabel}</Badge>
          <h1 className="font-heading text-2xl tracking-tight text-balance sm:text-3xl">
            {session.name}
          </h1>
          {/* Depuis quand on attend cette personne là-dessus. Sur une formation
              qu'on n'a pas choisie, c'est une information qui situe. */}
          {session.myEnrolledAt != null && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('home.assignedOn', {
                date: formatSessionDate(session.myEnrolledAt, i18n.language, timezone, DATE_WITH_YEAR),
              })}
            </p>
          )}
        </div>
        {canDownloadCertificate(session) && (
          <Button onClick={onDownloadCertificate} disabled={downloadingCertificate} className="w-full md:w-auto">
            {downloadingCertificate
              ? <Loader className="mr-2 size-4 animate-spin" />
              : <Award className="mr-2 size-4" />}
            {t('home.certificate.download')}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Progress value={progressPct(session)} className="h-2 min-w-40 flex-1" aria-label={t('path.progress.label')} />
        <span className="text-sm text-muted-foreground">
          {t('home.stepsDone', { done, total })}
        </span>
        {remaining && (
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="size-3.5" />
            {t('home.meta.remaining', { duration: remaining })}
          </span>
        )}
      </div>

      <CertificateLockNotice session={session} />
      {/*
        Le mode de progression existait en base depuis #139 et ne se voyait
        nulle part : l'apprenant découvrait l'ordre imposé en butant dessus.
      */}
      <p className="text-sm text-muted-foreground">
        {session.progressionMode === 'SEQUENTIAL'
          ? t('path.mode.sequential')
          : t('path.mode.free')}
      </p>
    </div>
  )
}

export default PathHeader
